import { formatHours, formatInteger, formatPercent } from '../format/number.js';

/**
 * Katalog over de taerskler en regel laeser fra `codepass_domain_rules.thresholds`.
 *
 * Kolonnen er `jsonb` og siger derfor intet om hvilke noegler der betyder noget, hvad de
 * hedder paa dansk, eller hvad der er en meningsfuld vaerdi. Det staar her - ét sted, som
 * baade API'et validerer imod og formularen paa /admin/regler tegner ud fra. To lister ville
 * betyde, at en admin kunne skrive en taerskel, ingen regel laeser.
 *
 * Noeglerne er reglens egne og skal staves praecis som reglen laeser dem med
 * `numberThreshold` - fx `advarsel_pct` i apps/api/src/domain/rules/kontraktloft.ts. En
 * stavefejl her rammer ingen taerskel: reglen falder tilbage paa sin standard, og graensen
 * admin lige satte, gaelder ingenting. Det er den vaerste maade et vaern kan gaa i staa paa,
 * fordi intet fejler.
 *
 * **Standarderne skal stemme med reglens egne konstanter.** `DEFAULT_WARN_PCT` og de andre er
 * det, reglen faktisk regner med, naar noeglen ikke er sat. Tallet staar her, fordi UI'et skal
 * kunne sige hvad et tomt felt betyder - ikke for at afgøre det.
 *
 * En regel uden taerskler staar ikke i kataloget. Todotjekker, Kontraktverifikation og
 * Scopeverifikation har ingen graenser at saette: de rejser alarm paa en manglende kobling
 * eller paa to kilder, der ikke siger det samme, og dér er svaret ja eller nej. En graense
 * ville kun flytte spoergsmaalet om, hvor uenige to systemer maa vaere.
 */

/** Enheden bag tallet. Styrer baade visning og hvad feltet maaler i. */
export const RULE_THRESHOLD_UNITS = ['pct', 'timer', 'dage', 'maaneder'] as const;
export type RuleThresholdUnit = (typeof RULE_THRESHOLD_UNITS)[number];

export interface RuleThresholdDef {
  /** Noeglen i `thresholds`, som reglen laeser den. */
  key: string;
  /** Dansk label i UI'et. */
  label: string;
  unit: RuleThresholdUnit;
  /**
   * Hvad reglen regner med, naar noeglen ikke er sat. Skal stemme med reglens konstant.
   * Et tomt felt i formularen fjerner noeglen og lader reglen falde tilbage paa denne.
   *
   * **Null er ingen graense**, ikke nul: reglen goer intet for noeglen, naar den ikke er sat.
   * Auditoprydningen bruger det - en entitet uden et antal dage slettes aldrig, og en standard
   * ville betyde, at reglen slettede noget, ingen havde bedt om.
   */
  fallback: number | null;
  /** Hvad et tomt felt betyder, naar `fallback` er null. Dansk - den staar under feltet. */
  emptyHint?: string;
  /** Kun hele tal - fx et antal dage. */
  integer?: boolean;
  min: number;
  max: number;
  /**
   * Noeglen denne taerskel ikke maa ligge under.
   *
   * Findes for én slags fejl, som ellers ikke ser ud som en fejl: staar `kritisk_pct` lavere
   * end `advarsel_pct`, rejses alarmen som *kritisk* i baandet under advarselsgraensen - se
   * `hoursCapUsage`, som tjekker den kritiske graense foerst. Reglen ville stadig koere, og
   * alle dens alarmer ville vaere kritiske.
   */
  atLeast?: string;
  /** Kort forklaring under feltet. Dansk - den staar i UI'et. */
  hint?: string;
}

export const RULE_THRESHOLDS: Readonly<Record<string, readonly RuleThresholdDef[]>> = {
  timetjek: [
    {
      key: 'advarsel_pct',
      label: 'Advarsel ved',
      unit: 'pct',
      fallback: 100,
      min: 1,
      max: 500,
      hint: 'Andel af opgavens estimat der udløser alarmen. 100 % er præcis over estimatet.',
    },
    {
      key: 'kritisk_pct',
      label: 'Kritisk ved',
      unit: 'pct',
      fallback: 150,
      min: 1,
      max: 500,
      atLeast: 'advarsel_pct',
      hint: 'Herover hæves alarmen til kritisk, uanset reglens egen alvorsgrad.',
    },
    {
      key: 'min_estimat',
      label: 'Mindste estimat',
      unit: 'timer',
      /*
       * Ikke 0: et estimat paa nul ville gøre forbrug/estimat til Infinity, som er over enhver
       * taerskel - og reglen ville rejse en alarm om hver opgave uden et estimat.
       */
      min: 0.25,
      fallback: 1,
      max: 1000,
      hint: 'Opgaver med et mindre estimat måles ikke — et kvarter er overskredet efter 16 minutter.',
    },
  ],

  /* Defaults must match apps/api/src/domain/rules/serviceopgavetjek.ts. */
  serviceopgavetjek: [
    {
      key: 'advarsel_pct',
      label: 'Advarsel over',
      unit: 'pct',
      fallback: 100,
      min: 1,
      max: 500,
      hint:
        'Andel af serviceopgavens estimat i Orchestrator, som timerne i OnePro skal over, før ' +
        'alarmen lyder. 100 % er estimatet.',
    },
    {
      key: 'kritisk_pct',
      label: 'Kritisk ved',
      unit: 'pct',
      fallback: 150,
      min: 1,
      max: 500,
      atLeast: 'advarsel_pct',
      hint: 'Herover hæves alarmen til kritisk, uanset reglens egen alvorsgrad.',
    },
  ],

  kontraktloft: [
    {
      key: 'advarsel_pct',
      label: 'Advarsel ved',
      unit: 'pct',
      fallback: 90,
      min: 1,
      max: 500,
      hint: 'Andel af timeloftet der udløser alarmen.',
    },
    {
      key: 'kritisk_pct',
      label: 'Kritisk ved',
      unit: 'pct',
      fallback: 100,
      min: 1,
      max: 500,
      atLeast: 'advarsel_pct',
      hint: 'Herover hæves alarmen til kritisk. 100 % er præcis det aftalte loft.',
    },
    {
      key: 'min_loft',
      label: 'Mindste timeloft',
      unit: 'timer',
      fallback: 1,
      min: 0,
      max: 1000,
      hint: 'Aftaler med et mindre loft måles ikke — en halv time er over 90 % efter 27 minutter.',
    },
  ],

  milepaelstjek: [
    {
      key: 'advarsel_pct',
      label: 'Advarsel ved',
      unit: 'pct',
      fallback: 100,
      min: 1,
      max: 500,
      hint: 'Andel af milepælens estimat der udløser alarmen.',
    },
    {
      key: 'kritisk_pct',
      label: 'Kritisk ved',
      unit: 'pct',
      fallback: 150,
      min: 1,
      max: 500,
      atLeast: 'advarsel_pct',
      hint: 'Herover hæves alarmen til kritisk.',
    },
  ],

  timepristjekker: [
    {
      key: 'min_pct',
      label: 'Nedre grænse',
      unit: 'pct',
      fallback: 90,
      min: 1,
      /*
       * Over 100 % ville betyde, at den reelle timepris skal ligge *over* den aftalte, foer
       * reglen er tilfreds - og saa rejser den en alarm paa hver eneste aftale.
       */
      max: 100,
      hint: 'Falder den reelle timepris under denne andel af den aftalte, rejses alarmen.',
    },
    {
      key: 'min_timer',
      label: 'Mindste antal timer',
      unit: 'timer',
      fallback: 5,
      min: 0,
      max: 1000,
      hint: 'Aftaler med færre fakturerede timer er for tyndt et grundlag til at måle en pris på.',
    },
  ],

  /*
   * Oprydningen: dage efter hvilke en post i audit-loggen slettes, pr. `entity_type`.
   *
   * **Kataloget er ogsaa listen over, hvad reglen maa roere.** Reglen laeser kun de noegler, der
   * staar her - se domain/rules/auditoprydning.ts - og en entitet, der ikke har et felt, kan
   * derfor ikke slettes ved en tastefejl i en jsonb-kolonne. En ny entitetstype er én linje mere.
   */
  auditoprydning: [
    auditRetention('user', 'Brugere', {
      hint:
        'Logins og brugeradministration. Hver brugers seneste login bevares, så ' +
        '"Sidst logget ind" stadig er rigtig.',
    }),
    auditRetention('invoice', 'Fakturaer', {
      hint: 'Oprettelse, godkendelse, afsendelse og annullering af fakturaer.',
    }),
    auditRetention('alert', 'Alarmer', {
      hint: 'Behandling af alarmer: taget, udsat, løst og afvist.',
    }),
    auditRetention('domain_rule', 'Regler', {
      hint: 'Ændringer af regler og manuelle kørsler ("Kør nu").',
    }),
    auditRetention('integration_setting', 'Integrationsnøgler', {
      hint: 'Visning, ændring og sletning af nøgler på /admin/integrationer.',
    }),
    auditRetention('setting', 'Indstillinger', {
      hint: 'TV-dashboardets visning, budget, faktureringsgrad og kalender på /admin/tv-dashboard.',
    }),
  ],

  /*
   * Oprydningen i alarm-arkivet: dage efter lukningen, hvorefter en alarm slettes, pr. status.
   *
   * Samme form som auditoprydningen, og kataloget er ogsaa her listen over, hvad reglen maa
   * roere - se domain/rules/alarmoprydning.ts. Kun de lukkede statusser staar her: en aaben alarm
   * er noget, nogen skal handle paa, og den slettes aldrig.
   */
  alarmoprydning: [
    alarmRetention(
      'resolved',
      'Løste alarmer',
      'Alarmer markeret som løst. Gælder forholdet stadig, når alarmen er slettet, rejses den ' +
        'igen som en ny alarm.',
    ),
    alarmRetention(
      'dismissed',
      'Afviste alarmer',
      'Alarmer afvist som ikke relevante. Det er den afviste alarm, der holder forholdet væk — ' +
        'er den slettet, og gælder forholdet stadig, rejses det igen som en ny alarm.',
    ),
  ],

  kundeaktivitet: [
    {
      key: 'maaneder',
      label: 'Alarm efter',
      unit: 'maaneder',
      fallback: 6,
      integer: true,
      min: 1,
      max: 60,
      hint:
        'Så mange hele måneder uden registreret arbejde på kundens aftaler, før alarmen lyder. ' +
        'Arbejde er en opgave meldt færdig i OnePro med timer på.',
    },
  ],

  fravaerskalender: [
    {
      key: 'dage',
      label: 'Alarm efter',
      unit: 'dage',
      fallback: 30,
      integer: true,
      min: 1,
      max: 365,
      hint:
        'Så mange dage uden en ny eller ændret begivenhed i fraværskalenderen, før alarmen lyder. ' +
        'En stille kalender og en synkronisering i stå ser ens ud, så sæt den ikke for lavt.',
    },
  ],

  /* Defaults must match apps/api/src/domain/rules/kvartalsfakturering.ts. */
  kvartalsfakturering: [
    {
      key: 'min_pct',
      label: 'For lidt under',
      unit: 'pct',
      fallback: 90,
      min: 1,
      max: 200,
      hint:
        'Faktureret i kvartalet i procent af det, budgettet i Business Central forventer til ' +
        'dato — den hvide streg på TV-skærmens kvartalskort. Under denne andel lyder alarmen.',
    },
    {
      key: 'efter_dage',
      label: 'Første måling efter',
      unit: 'dage',
      fallback: 14,
      integer: true,
      min: 0,
      max: 90,
      hint:
        'Så mange dage inde i kvartalet, før reglen måler. De første dage er der næsten intet ' +
        'faktureret at måle på, og hvert kvartal ville begynde med en alarm.',
    },
  ],

  /* Default must match apps/api/src/domain/rules/applikationsdrift.ts. */
  applikationsdrift: [
    {
      key: 'dage',
      label: 'Alarm efter',
      unit: 'dage',
      fallback: 2,
      integer: true,
      min: 0,
      max: 27,
      hint:
        'Så mange dage efter den 1., før en manglende applikationsdrift, app eller et ' +
        'abonnement giver alarm — for et kvartals- eller årsabonnement den 1. i periodens første ' +
        'måned. Den automatiske fakturering prøver igen næste morgen, hvis den blev standset den 1.',
    },
  ],

  /* Default must match apps/api/src/domain/rules/forfaldstjek.ts. */
  forfaldstjek: [
    {
      key: 'dage',
      label: 'Alarm efter',
      unit: 'dage',
      fallback: 1,
      integer: true,
      min: 1,
      max: 90,
      hint:
        'Så mange dage efter forfaldsdatoen, før en bogført faktura, der ikke er betalt, giver ' +
        'alarm. 1 er dagen efter forfald. Betalt er det, Business Central siger — en indbetaling, ' +
        'bogholderiet ikke har udlignet endnu, tæller ikke.',
    },
  ],
};

/**
 * Et felt paa auditoprydningen: antal dage for én entitetstype.
 *
 * Fra én dag til ti aar. Hele dage, fordi det er det, en opbevaringsfrist aftales i, og fordi
 * `180,5` dage ikke er en frist nogen har besluttet.
 */
function auditRetention(key: string, label: string, extra: { hint: string }): RuleThresholdDef {
  return {
    key,
    label,
    unit: 'dage',
    fallback: null,
    emptyHint: 'Tomt felt = posterne slettes aldrig.',
    integer: true,
    min: 1,
    max: 3650,
    hint: `${extra.hint} Entitet: ${key}.`,
  };
}

/**
 * Et felt paa alarmoprydningen: antal dage efter lukningen for én status. Samme interval og
 * samme "tomt er aldrig" som auditoprydningen.
 */
function alarmRetention(key: string, label: string, hint: string): RuleThresholdDef {
  return {
    key,
    label,
    unit: 'dage',
    fallback: null,
    emptyHint: 'Tomt felt = alarmerne slettes aldrig.',
    integer: true,
    min: 1,
    max: 3650,
    hint,
  };
}

/** Taersklerne reglen kender. Tom for en regel uden graenser at saette. */
export function ruleThresholds(rule: string): readonly RuleThresholdDef[] {
  return RULE_THRESHOLDS[rule] ?? [];
}

export function ruleThreshold(rule: string, key: string): RuleThresholdDef | undefined {
  return ruleThresholds(rule).find((threshold) => threshold.key === key);
}

/**
 * Taerskelvaerdierne som de ligger i kolonnen.
 *
 * Kun tal: hver vaerdi sammenlignes med et tal i reglen, og en streng ville blive `NaN`.
 * En noegle der ikke staar her, betyder reglens standard - ikke nul.
 */
export type RuleThresholdValues = Record<string, number>;

export interface RuleThresholdProblem {
  key: string;
  /** Dansk besked, vist ved feltet og i serverens fejlsvar. */
  message: string;
}

/**
 * Den vaerdi reglen faktisk regner med: den gemte, ellers standarden fra kataloget.
 *
 * Samme opslag som `numberThreshold` i API'et laver, saa UI'et kan vise det, reglen vil gøre,
 * og ikke kun det, der staar i kolonnen. Null for en noegle kataloget ikke kender - der er
 * ingen standard at falde tilbage paa.
 */
export function effectiveRuleThreshold(
  rule: string,
  key: string,
  values: RuleThresholdValues | null | undefined,
): number | null {
  const value = Number(values?.[key]);
  if (Number.isFinite(value)) return value;
  return ruleThreshold(rule, key)?.fallback ?? null;
}

/**
 * Hvad der er i vejen for at gemme taersklerne.
 *
 * Tom liste = i orden. Koeres to steder: i formularen, saa fejlen staar ved feltet, og paa
 * serveren, hvor den er autoritativ - jf. CLAUDE.md. Samme funktion, saa de to ikke kan naa
 * hver sin konklusion.
 *
 * **Ukendte noegler er ikke en fejl.** Tabellen deles med de andre OneCode-apps, og en noegle
 * kataloget ikke kender, kan vaere en anden apps eller en nyere regels. Reglen ignorerer den,
 * og en afvisning her ville laase admin ude af at rette de taerskler, der *er* kendte.
 */
export function validateRuleThresholds(
  rule: string,
  values: RuleThresholdValues | null | undefined,
): RuleThresholdProblem[] {
  const problems: RuleThresholdProblem[] = [];
  if (!values) return problems;

  for (const [key, raw] of Object.entries(values)) {
    const def = ruleThreshold(rule, key);
    if (!def) continue;

    const value = Number(raw);
    if (!Number.isFinite(value)) {
      problems.push({ key, message: `${def.label} skal være et tal.` });
      continue;
    }

    if (def.integer && !Number.isInteger(value)) {
      problems.push({ key, message: `${def.label} skal være et helt tal.` });
      continue;
    }

    if (value < def.min || value > def.max) {
      problems.push({
        key,
        message:
          `${def.label} skal ligge mellem ` +
          `${formatRuleThresholdValue(def.unit, def.min)} og ` +
          `${formatRuleThresholdValue(def.unit, def.max)}.`,
      });
      continue;
    }

    if (!def.atLeast) continue;

    /*
     * Den anden taerskels *gaeldende* vaerdi og ikke kun den gemte: er `advarsel_pct` tomt,
     * regner reglen med sin standard, og den skal graensen holdes op mod. Ellers ville et
     * tomt advarselsfelt lade en kritisk graense under standarden slippe igennem.
     */
    const floor = effectiveRuleThreshold(rule, def.atLeast, values);
    const floorDef = ruleThreshold(rule, def.atLeast);

    if (floor !== null && floorDef && value < floor) {
      problems.push({
        key,
        message:
          `${def.label} må ikke ligge under ${floorDef.label.toLowerCase()} ` +
          `(${formatRuleThresholdValue(def.unit, floor)}) — så ville alle alarmer blive ` +
          `kritiske.`,
      });
    }
  }

  return problems;
}

/**
 * Tallet som det vises: procent med haardt mellemrum, timer med to decimaler.
 *
 * Ligger her og ikke i en komponent, fordi formatering hoerer i packages/shared - jf.
 * CLAUDE.md. Hele tal skrives uden decimaler: '90 %' og ikke '90,00 %', fordi en taerskel
 * saettes i hele procent i praksis.
 */
export function formatRuleThresholdValue(unit: RuleThresholdUnit, value: number): string {
  if (unit === 'pct') return formatPercent(value, Number.isInteger(value) ? 0 : 2);
  if (unit === 'dage') return `${formatInteger(value)} ${value === 1 ? 'dag' : 'dage'}`;
  if (unit === 'maaneder') return `${formatInteger(value)} ${value === 1 ? 'måned' : 'måneder'}`;
  return `${formatHours(value)} timer`;
}

/** Taersklen som én linje: 'Advarsel ved 90 %'. */
export function formatRuleThreshold(def: RuleThresholdDef, value: number): string {
  return `${def.label} ${formatRuleThresholdValue(def.unit, value)}`;
}

/**
 * Hvad der staar i feltet.
 *
 * Tre udfald og ikke to: `empty` betyder **ingen noegle** og dermed reglens standard - ikke
 * nul. En taerskel paa 0 og ingen taerskel er to forskellige beslutninger, og et felt der
 * oversatte det ene til det andet, ville saette `min_loft` til 0 og maale paa aftaler med et
 * loft paa fem minutter. `invalid` holdes ude fra vaerdierne, saa der aldrig skrives `NaN` i
 * kolonnen - en regel, der sammenligner mod NaN, holder op med at rejse alarmer uden at fejle.
 */
export type ParsedRuleThreshold =
  { kind: 'empty' } | { kind: 'invalid' } | { kind: 'value'; value: number };

/** Komma accepteres: et dansk tastatur skriver `92,5`. */
export function parseRuleThresholdInput(raw: string): ParsedRuleThreshold {
  const normalized = raw.trim().replace(',', '.');
  if (normalized.length === 0) return { kind: 'empty' };

  const value = Number(normalized);
  return Number.isFinite(value) ? { kind: 'value', value } : { kind: 'invalid' };
}

/**
 * Formularens felter som det, der skal staa i kolonnen - og det, der er i vejen.
 *
 * Ligger her og ikke i komponenten, saa formularen kun tegner felter: hvad et tomt felt
 * betyder, og hvornaar en taerskel er ugyldig, er samme beslutning som serveren traeffer, og
 * to steder ville betyde to svar. Komponenten sender teksterne ind og faar `values` til
 * kroppen og `problems` til felterne.
 *
 * Tomme felter kommer ikke med i `values`: noeglen udgaar, og reglen falder tilbage paa sin
 * standard. Er alle felter tomme, er `values` tomt - og kaldstedet gemmer da NULL, saa "ingen
 * taerskler" er én vaerdi og ikke to.
 */
export function readRuleThresholdInputs(
  rule: string,
  inputs: Readonly<Record<string, string>>,
): { values: RuleThresholdValues; problems: RuleThresholdProblem[] } {
  const values: RuleThresholdValues = {};
  const problems: RuleThresholdProblem[] = [];

  for (const [key, raw] of Object.entries(inputs)) {
    const parsed = parseRuleThresholdInput(raw);
    if (parsed.kind === 'empty') continue;

    if (parsed.kind === 'invalid') {
      const label = ruleThreshold(rule, key)?.label ?? key;
      problems.push({ key, message: `${label} skal være et tal.` });
      continue;
    }

    values[key] = parsed.value;
  }

  return { values, problems: [...problems, ...validateRuleThresholds(rule, values)] };
}
